import { faPhotoVideo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { saveAs } from "file-saver";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Modal, Popup } from "semantic-ui-react";

import { DesktopOnly, MobileOnly } from "../../../../../layout";
import { ANSWERS } from "../../../../../util/answers";
import { calculateVatGroups, getInterventionPriceWithVAT, getInterventionTotals, round2 } from "../../../../../util/common";
import { getTyreItemName } from "../../../../../util/tyre";
import ErrorModal from "../../../components/ErrorModal";

import "./index.scss";

const Order = ({
  readOnly,
  approvedInterventions,
  pendingInterventions,
  settings,
  replacementItems,
  remarks,
  agreements,
  approved,
  onAgreed,
  allowedToOrder,
  onSendAnswer,
  onGoToSuccessScreen,
  showAgreementError,
  onShowAgreementError,
  onShowCustomerNameRequiredError,
  onCustomerNameChange,
  customerName,
  customerNameVisible,
  showCustomerNameRequiredError,
  bill,
  acsesIsAvailable,
  onDropInAcsesChange,
  dropInAcsesLocker,
  showMaintenanceModal,
}) => {
  const { t } = useTranslation();
  const { include_vat, vat } = settings;
  const [visibleAgreement, setVisibleAgreement] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [openErrorModal, setOpenErrorModal] = React.useState(false);

  const displayPrice = price => {
    if (!(price > 0.01)) return null;

    if (include_vat) price = price * (1 + vat / 100);

    return <span className="order-scheduled_services-item-price">€ {round2(price).toFixed(2)}</span>;
  };

  const displayInterventionPrice = intervention => {
    if (!(intervention.price > 0.01)) return null;

    const price = include_vat ? getInterventionPriceWithVAT(intervention, settings) : intervention.price;

    return <span className="order-scheduled_services-item-price">€ {round2(price).toFixed(2)}</span>;
  };

  const renderApprovedServices = () => {
    return approvedInterventions.map(intervention => (
      <Popup
        key={intervention.id}
        trigger={
          <div key={"trigger" + intervention.id} className="order-scheduled_services-item">
            <span className="order-scheduled_services-item-title text-ellipsis">{intervention.title}</span>
            {displayInterventionPrice(intervention)}
          </div>
        }
        content={
          <div key={"content" + intervention.id} className="order-scheduled_services-item">
            <span className="order-scheduled_services-item-title">{intervention.title}</span>
          </div>
        }
      />
    ));
  };

  const renderPendingServicesToFix = () => {
    return pendingInterventions
      .filter(intervention => intervention.communication_result_status === ANSWERS.ADD_TO_CART)
      .map(intervention => (
        <Popup
          key={intervention.id}
          trigger={
            <div key={"trigger" + intervention.id} className="order-scheduled_services-item">
              <span className="order-scheduled_services-item-title text-ellipsis">{intervention.title}</span>
              {displayInterventionPrice(intervention)}
            </div>
          }
          content={
            <div key={"content" + intervention.id} className="order-scheduled_services-item">
              <span className="order-scheduled_services-item-title">{intervention.title}</span>
            </div>
          }
        />
      ));
  };

  const renderPendingServicesToContact = () => {
    return pendingInterventions
      .filter(intervention => intervention.communication_result_status === ANSWERS.CONTACT_ME)
      .map(intervention => (
        <Popup
          key={intervention.id}
          trigger={
            <div key={"trigger" + intervention.id} className="order-scheduled_services-item">
              <span className="order-scheduled_services-item-title text-ellipsis">{intervention.title}</span>
            </div>
          }
          content={
            <div key={"content" + intervention.id} className="order-scheduled_services-item">
              <span className="order-scheduled_services-item-title">{intervention.title}</span>
            </div>
          }
        />
      ));
  };

  const renderReplacementItemsToFix = () => {
    return replacementItems
      .filter(t => t.replacements?.some(r => r.communication_result_status === ANSWERS.ADD_TO_CART))
      .map(item => {
        const selectedReplacement = item.replacements.find(r => r.selected);

        return (
          <Popup
            key={item.id}
            trigger={
              <div key={"trigger" + item.id} className="order-scheduled_services-item">
                <span className="order-scheduled_services-item-title text-ellipsis">{getTyreItemName(t, item)}</span>
                {displayPrice(selectedReplacement.price)}
              </div>
            }
            content={
              <div key={"content" + item.id} className="order-scheduled_services-item">
                <span className="order-scheduled_services-item-title">{getTyreItemName(t, item)}</span>
              </div>
            }
          />
        );
      });
  };

  const renderReplacementContactItems = () => {
    return replacementItems
      .filter(t => t.replacements?.some(r => r.communication_result_status === ANSWERS.CONTACT_ME))
      .map(item => {
        return (
          <div key={item.id} className="order-scheduled_services-item">
            <span className="order-scheduled_services-item-title">{getTyreItemName(t, item)}</span>
          </div>
        );
      });
  };

  const renderRemarks = () => {
    return remarks
      .filter(r => r.title || r.description || r.attachments.length > 0)
      .map((remark, i) => {
        let title = remark.title;
        if (!title && remark.description) {
          title = remark.description.substr(0, 35);
          if (remark.description.length > 35) title += "...";
        }

        return (
          <div key={"remark" + i} className="order-mobile-remarks-container">
            <span className="order-mobile-remarks-title">{title}</span>
            {remark.attachments.length > 0 && (
              <span>
                <FontAwesomeIcon icon={faPhotoVideo} className="order-mobile-remarks-icon" />
                {remark.attachments.length}
              </span>
            )}
          </div>
        );
      });
  };

  const getTotalAmount = () => {
    let total = 0;
    const vatRate = include_vat ? 1 + settings.vat / 100 : 1;

    const allInterventions = [
      ...(Array.isArray(approvedInterventions) ? approvedInterventions.filter(i => i.price > 0.01) : []),
      ...(Array.isArray(pendingInterventions) ? pendingInterventions.filter(i => i.communication_result_status === ANSWERS.ADD_TO_CART && i.price > 0.01) : []),
    ];

    const interventionTotals = getInterventionTotals(allInterventions, settings);
    total += interventionTotals.totalAmount;

    let additionalPrices = 0;

    if (Array.isArray(replacementItems)) {
      total += replacementItems.reduce((prev, cur) => {
        const selectedReplacement = cur.replacements?.find(r => r.selected && r.communication_result_status === ANSWERS.ADD_TO_CART);

        if (selectedReplacement?.price > 0.01) {
          additionalPrices += selectedReplacement.price;
          return prev + round2(selectedReplacement.price * vatRate);
        }
        return prev;
      }, 0);
    }

    return { total, additionalPrices, interventionTotals };
  };

  const handleAgreementChecked = id => {
    if (readOnly) return;

    onAgreed(id);
  };

  const downloadBill = async () => {
    try {
      const response = await axios.get(bill.url, { responseType: "blob" });
      saveAs(new Blob([response.data]), bill.name);
    } catch (error) {
      console.error("Error downloading the bill:", error);
    }
  };

  const handleSendAnswer = () => {
    if (readOnly || loading || !allowedToOrder()) return;

    onShowAgreementError && onShowAgreementError(!approved);
    onShowCustomerNameRequiredError && onShowCustomerNameRequiredError(customerNameVisible && !customerName);
    setOpenErrorModal(!approved);

    if (!approved || (customerNameVisible && !customerName)) return;

    let promise = onSendAnswer();
    if (promise) {
      setLoading(true);
      promise
        .then(result => {
          const { keylocker_pin, acses_pin, acses_locker_name } = result?.data?.data || { keylocker_pin: null, acses_pin: null, acses_locker_name: null };
          setLoading(false);
          onGoToSuccessScreen({ keylocker_pin, acses_pin, acses_locker_name });
        })
        .catch(error => {
          console.log("handleSendAnswer error", error);
          setLoading(false);

          const errorMessage = error?.response?.data?.errors?.shift();

          if (errorMessage?.includes("maintenance_mode")) {
            showMaintenanceModal();
          } else {
            setOpenErrorModal(true);
          }
        });
    }
  };

  const isOrderEmpty =
    !approvedInterventions?.length &&
    !pendingInterventions?.some(i => [ANSWERS.ADD_TO_CART, ANSWERS.CONTACT_ME].includes(i.communication_result_status)) &&
    !replacementItems?.some(ri => ri.replacements?.some(r => r.selected)) &&
    !remarks?.some(r => r.title || r.description || r.attachments.length > 0);

  const { total: totalAmount, additionalPrices, interventionTotals } = getTotalAmount();
  const allVatGroups = include_vat ? calculateVatGroups(interventionTotals.vatGroups, additionalPrices, settings) : [];
  const nonZeroVatGroups = allVatGroups.filter(group => group.rate > 0 && group.amount > 0);

  return (
    <>
      <Modal open={visibleAgreement !== null} size="fullscreen" detachable={false}>
        <Modal.Actions>
          <Button onClick={() => setVisibleAgreement(null)} negative icon="times" />
        </Modal.Actions>
        <Modal.Content scrolling>
          <div dangerouslySetInnerHTML={{ __html: visibleAgreement }} />
        </Modal.Content>
      </Modal>

      <MobileOnly>
        <h4 className="mobile-order-header">{t("cc_YOUR_SUMMARY").message || "YOUR SUMMARY"}</h4>
      </MobileOnly>

      <div className="order-container">
        {isOrderEmpty && (
          <MobileOnly>
            <p className="order-nothing-for-approval">
              {t("cc_nothing_extra_to_approve").message || "There is nothing extra to approve on your car, we will update you when the car is ready."}
            </p>
          </MobileOnly>
        )}
        {Array.isArray(approvedInterventions) && approvedInterventions.length > 0 && (
          <div>
            <h4 className="order-scheduled_services-heading">{t("cc_approved_services").message || "Approved services"}</h4>
            <div className="order-scheduled_services-container">{renderApprovedServices()}</div>
          </div>
        )}

        {Array.isArray(pendingInterventions) && pendingInterventions.length > 0 && pendingInterventions.some(i => i.communication_result_status === ANSWERS.ADD_TO_CART) && (
          <div>
            <h4 className="order-scheduled_services-heading">{t("cc_additional_services").message || "Additional services"}</h4>
            <div className="order-scheduled_services-container">{renderPendingServicesToFix()}</div>
          </div>
        )}

        {Array.isArray(pendingInterventions) && pendingInterventions.length > 0 && pendingInterventions.some(i => i.communication_result_status === ANSWERS.CONTACT_ME) && (
          <div>
            <h4 className="order-scheduled_services-heading">{t("cc_you_will_be_contacted_for").message || "You will be contacted for"}</h4>
            <div className="order-scheduled_services-container">{renderPendingServicesToContact()}</div>
          </div>
        )}

        {Array.isArray(replacementItems) && replacementItems.some(ri => ri.replacements?.some(r => r.selected)) && (
          <div>
            <h4 className="order-scheduled_services-heading">{t("cc_tyre_replacements").message || "Tyre replacements"}</h4>

            {replacementItems.some(ri => ri.replacements?.some(r => r.communication_result_status === ANSWERS.ADD_TO_CART)) && (
              <div>
                <h4 className="order-scheduled_services-heading">{t("cc_approved_replacements").message || "Approved replacements"}</h4>
                <div className="order-scheduled_services-container">{renderReplacementItemsToFix()}</div>
              </div>
            )}

            {replacementItems.some(ri => ri.replacements?.some(r => r.communication_result_status === ANSWERS.CONTACT_ME)) && (
              <div>
                <h4 className="order-scheduled_services-heading">{t("cc_you_will_be_contacted_for").message || "You will be contacted for"}</h4>
                <div className="order-scheduled_services-container">{renderReplacementContactItems()}</div>
              </div>
            )}
          </div>
        )}

        {Array.isArray(remarks) && remarks.some(r => r.title || r.description || r.attachments.length > 0) && (
          <MobileOnly>
            <h4 className="order-optional_services-heading">{t("cc_remarks").message || "Remarks"}</h4>
            <div className="order-optional_services-remarks">{renderRemarks()}</div>
          </MobileOnly>
        )}
        {totalAmount > 0.01 && (
          <div className="order-total-amount-container">
            {include_vat &&
              allVatGroups.length > 1 &&
              allVatGroups.map(group => (
                <div key={"total-" + group.rate} className="order-total-amount-vat-container">
                  <span className="order-total-amount-vat-title">
                    {t("cc_total_with").message || "Total with"} {group.rate}% {t("cc_vat").message || "VAT"}
                  </span>
                  <span className="order-total-amount-vat-amount">€ {group.total.toFixed(2)}</span>
                </div>
              ))}
            <div className="order-total-amount-money-container">
              <span className="order-total-amount-money-title">{`${t("cc_TOTAL_AMOUNT").message || "TOTAL AMOUNT"}:`}</span>
              <span className="order-total-amount-money-amount">€ {totalAmount.toFixed(2)}</span>
            </div>
            {include_vat &&
              nonZeroVatGroups.map(group => (
                <div key={"vat-" + group.rate} className="order-total-amount-vat-container">
                  <span className="order-total-amount-vat-title">
                    *{t("cc_including").message || "Including"} {group.rate}% {t("cc_vat").message || "VAT"}
                  </span>
                  <span className="order-total-amount-vat-amount">€ {group.amount.toFixed(2)}</span>
                </div>
              ))}

            {bill && (
              <div className="download-bill-link" onClick={downloadBill}>
                {t("cc_download_your_bill").message || "Download your bill"}
              </div>
            )}
          </div>
        )}

        {Array.isArray(agreements) && (
          <div className="order-terms-container">
            {agreements.map(a => (
              <React.Fragment key={a.id}>
                <div className="order-terms-item">
                  <Checkbox defaultChecked={a.accepted} disabled={readOnly} onChange={() => handleAgreementChecked(a.id)} className="big-box" />
                  <Button className="order-terms-btn" onClick={() => setVisibleAgreement(a.text)}>
                    {a.name}
                  </Button>
                </div>
                <div>
                  {!a.accepted && !a.optional_customcom && showAgreementError && (
                    <div className="order-terms-error">{t("cc_accept_agreements").message || "*Please accept the agreement above to continue!"}</div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {!readOnly && acsesIsAvailable && (
          <div className="acses-container">
            <Checkbox checked={dropInAcsesLocker} onChange={onDropInAcsesChange} className="big-box" />
            <label>{t("cc_book_automated_locker_to_drop_key").message || "Book an automated locker to drop the car key"}</label>
          </div>
        )}

        {!readOnly && customerNameVisible && (
          <div className={`customer-name-container ${showCustomerNameRequiredError ? "required" : ""} `}>
            <label>{t("cc_enter_your_name").message || "Please enter your name"}</label>
            <Input type="text" fluid name="customer_name" value={customerName} onChange={e => onCustomerNameChange(e.target.value)} />
          </div>
        )}

        {!readOnly && (
          <DesktopOnly className="order-confirm-container">
            <Button className="bg-color" loading={loading} onClick={handleSendAnswer}>
              {t("cc_SUBMIT").message || "SUBMIT"}
            </Button>
          </DesktopOnly>
        )}

        <ErrorModal
          isOpen={openErrorModal}
          onClose={() => setOpenErrorModal(false)}
          message={
            showAgreementError
              ? t("cc_accept_agreements").message || "*Please accept the agreement above to continue!"
              : t("cc_error_please_refresh").message || "Something went wrong. Please try to refresh the page if the problem persists."
          }
          onReload={showAgreementError ? null : () => window.location.reload(window.location.hash?.substring(1) || null)}
        />
      </div>
    </>
  );
};

export default Order;
